<h1 mat-dialog-title>{{title}}</h1>
<mat-dialog-content [formGroup]="modelForm" autocomplete="off">
  <p class="subtitle">{{subtitle}}</p>

  <section class="section">
    <h3>Details</h3>
    <mat-form-field class="full">
      <mat-label>Name</mat-label>
      <input type="text" matInput formControlName="name" placeholder="A unique, descriptive name for the model" [errorStateMatcher]="matcher" id="model-name" [maxlength]="MODEL_NAME_MAX" required>
      <mat-hint align="end">{{modelForm.value.name?.length || 0}}/{{MODEL_NAME_MAX}}</mat-hint>
      @if (modelForm.controls['name'].hasError('required')) {
        <mat-error>Name is <strong>required</strong></mat-error>
      }@else if (modelForm.controls['name'].invalid) {
        <mat-error>{{getNameError()}}</mat-error>
      }
    </mat-form-field>
    <mat-form-field class="full">
      <mat-label>Description</mat-label>
      <input type="text" matInput formControlName="description" placeholder="What part of the business this model represents" [errorStateMatcher]="matcher" id="model-description" [maxlength]="MODEL_DESCRIPTION_MAX">
      <mat-hint align="end">{{modelForm.value.description?.length || 0}}/{{MODEL_DESCRIPTION_MAX}}</mat-hint>
      @if (modelForm.controls['description'].hasError('maxlength')) {
        <mat-error>{{getDescriptionError()}}</mat-error>
      }
    </mat-form-field>

    <div class="pair">
      <mat-form-field>
        <mat-label>Function</mat-label>
        <mat-select formControlName="function" [errorStateMatcher]="matcher" required>
          @for (function of functions; track function) {
            <mat-option [value]="function">{{function.toString()}}</mat-option>
          }
        </mat-select>
        <mat-hint>The business function the model belongs to</mat-hint>
        @if (modelForm.controls['function'].invalid) {
          <mat-error>Choose a function from the list</mat-error>
        }
      </mat-form-field>
      <mat-form-field>
        <mat-label>Tags</mat-label>
        <mat-select multiple formControlName="tagKeys">
          @for (tag of getModelTags(); track tag._key) {
            <mat-option [value]="tag._key">{{tag.name}}</mat-option>
          }
        </mat-select>
        <mat-hint>{{getModelTags().length ? 'Used for filtering and searching' : 'No model tags have been set up'}}</mat-hint>
      </mat-form-field>
    </div>

    <div class="pair">
      <mat-form-field>
        <mat-label>Owner</mat-label>
        <mat-select formControlName="owner">
          <mat-option [value]="undefined">No owner</mat-option>
          @for (user of users; track user._key) {
            <mat-option [value]="user._key">{{user.name}}</mat-option>
          }
        </mat-select>
        <mat-hint>Who keeps the model up to date</mat-hint>
      </mat-form-field>
      <mat-form-field>
        <mat-label>Review date</mat-label>
        <input matInput [matDatepicker]="reviewPicker" formControlName="reviewDate">
        <mat-datepicker-toggle matSuffix [for]="reviewPicker"></mat-datepicker-toggle>
        <mat-datepicker #reviewPicker></mat-datepicker>
        <mat-hint>When the model should next be checked</mat-hint>
      </mat-form-field>
    </div>
  </section>

  <section class="section">
    <h3>Access</h3>
    <div class="toggle-row">
      <mat-slide-toggle formControlName="useOrganisationAccess" color="accent">Use organisation access</mat-slide-toggle>
      <span class="toggle-hint">
        @if (modelForm.value.useOrganisationAccess) {
          Users have the same access to this model as they do to the organisation. Turn off to choose access for each user.
        }@else {
          Choose each user's access to this model. Users set to No access can't open it, but can ask for access.
        }
      </span>
    </div>
    @if (!modelForm.value.useOrganisationAccess) {
      <app-user-access [userAccess]="model.userAccess"></app-user-access>
    }
  </section>

  @if (!data.isNew) {
    <section class="section">
      <h3>Backups</h3>
      <p class="toggle-hint">A model can have up to {{MAX_BACKUPS}} backups. Create one from the model toolbar with <mat-icon inline>backup</mat-icon>. Restoring a backup replaces the current model.</p>
      @if (hasBackups()) {
        <table mat-table [dataSource]="backupDataSource" class="backups">
          <ng-container matColumnDef="date">
            <td mat-cell *matCellDef="let backup" class="backup-date">{{backup.date | date:'d/M/yy, h:mm a'}}</td>
          </ng-container>
          <ng-container matColumnDef="description">
            <td mat-cell *matCellDef="let backup">{{backup.description}}</td>
          </ng-container>
          <ng-container matColumnDef="restore">
            <td mat-cell *matCellDef="let backup" class="backup-action">
              <button mat-icon-button color="accent" matTooltip="Replace the model with this backup" aria-label="Restore model backup" (click)="restoreBackup(backup)">
                <mat-icon>restore</mat-icon>
              </button>
            </td>
          </ng-container>
          <ng-container matColumnDef="delete">
            <td mat-cell *matCellDef="let backup" class="backup-action">
              <button mat-icon-button color="warn" matTooltip="Delete this backup permanently" aria-label="Delete model backup" (click)="deleteBackup(backup)">
                <mat-icon>delete</mat-icon>
              </button>
            </td>
          </ng-container>
          <tr mat-row *matRowDef="let row; columns: backupColumns;"></tr>
        </table>
      }@else {
        <p class="empty">There are no backups of this model.</p>
      }
    </section>
  }

  @if (!turnOffUpload) {
    <section class="section">
      <h3>Graph file</h3>
      @if (file) {
        <div class="file-row">
          <mat-icon>description</mat-icon>
          <span class="file-name">{{file.name}}</span>
          <button mat-icon-button matTooltip="Remove this file" aria-label="Remove graph file" (click)="clearFile()"><mat-icon>close</mat-icon></button>
        </div>
        @if (!data.isNew) {
          <div class="file-warning"><mat-icon>warning</mat-icon><span>Saving will replace the model's existing graph with this file.</span></div>
        }
      }@else {
        <p class="toggle-hint">Optionally load the model's graph from a LINQ model file (JSON).</p>
        <button mat-stroked-button color="accent" (click)="fileUpload.click()"><mat-icon>file_upload</mat-icon>Choose file</button>
      }
      <input type="file" class="file-input" accept="application/json,.json" (change)="onFileSelected($event)" #fileUpload>
    </section>
  }
</mat-dialog-content>

<div mat-dialog-actions align="end">
  <button mat-button (click)="dialogRef.close()">Cancel</button>
  <button mat-flat-button color="accent" (click)="save()">{{data.isNew?'Create model':'Save changes'}}</button>
</div>
